import { ChannelIcon, Dialog, ErrorState, Loading } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Field } from '../crm/forms';
import { api } from '../lib/api';

export interface ConnectorInfo {
  key: string;
  providers: string[];
  needs: 'shopify_domain' | 'site_url' | null;
  available: boolean;
  /** Development: signs in against the local sandbox, not the real platform. */
  sandbox?: boolean;
}

export const useConnectors = () =>
  useQuery({ queryKey: ['connectors'], queryFn: () => api<{ connectors: ConnectorInfo[] }>('/v1/connect') });

/**
 * One-click connect: asks for the store address when the platform needs it, then sends the browser to the
 * platform's own sign-in page. The platform sends it back to Integrations, where the accounts found are chosen.
 */
export function ConnectButton({
  connector,
  label,
  primary = true,
  testId,
}: {
  connector: ConnectorInfo;
  label: string;
  primary?: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const [shop, setShop] = useState('');
  const start = useMutation({
    mutationFn: (s?: string) =>
      api<{ url: string }>(`/v1/connect/${connector.key}/start`, {
        method: 'POST',
        body: JSON.stringify(s ? { shop: s } : {}),
      }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  if (!connector.available)
    return (
      <span className="badge badge-neutral" title={t('connect.notSetUpHint')}>
        {t('connect.notSetUp')}
      </span>
    );
  return (
    <>
      <button
        type="button"
        className={`btn btn-sm${primary ? ' btn-grad' : ''}`}
        onClick={() => (connector.needs ? setAsking(true) : start.mutate(undefined))}
        disabled={start.isPending}
        data-testid={testId ?? `connect-${connector.key}`}
      >
        <LogIn size={14} />
        {label}
        {connector.sandbox && (
          <span className="badge badge-neutral sandbox-badge" title={t('connect.sandboxHint')}>
            {t('connect.sandbox')}
          </span>
        )}
      </button>
      {start.isError && !asking && <ErrorState error={start.error} />}
      {asking && (
        <Dialog
          open
          title={t(`connect.shopTitle.${connector.key}`)}
          onClose={() => setAsking(false)}
          onSubmit={() => start.mutate(shop)}
          testId="connect-shop"
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setAsking(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-grad" disabled={start.isPending}>
                {t('connect.continue')}
              </button>
            </>
          }
        >
          <Field
            id="connect-shop-input"
            label={t(`connect.shopLabel.${connector.needs}`)}
            hint={t(`connect.shopHint.${connector.needs}`)}
          >
            <input
              id="connect-shop-input"
              className="input"
              dir="ltr"
              required
              autoFocus
              placeholder={
                connector.needs === 'shopify_domain' ? 'yourstore.myshopify.com' : 'https://shop.example.com'
              }
              value={shop}
              onChange={(e) => setShop(e.target.value)}
            />
          </Field>
          {start.isError && <ErrorState error={start.error} />}
        </Dialog>
      )}
    </>
  );
}

interface Session {
  id: string;
  connector: string;
  status: 'started' | 'authorised' | 'completed' | 'failed' | 'expired';
  error: string | null;
  assets: { provider: string; externalId: string; displayName: string; kind: string; connected: boolean }[];
}

/** Back from the platform: choose which of the accounts found to connect (all ticked by default). */
export function ConnectReturn() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('connect');
  const failed = params.get('connectError');
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const session = useQuery({
    queryKey: ['connect-session', sessionId],
    enabled: !!sessionId,
    queryFn: () => api<Session>(`/v1/connect/sessions/${sessionId}`),
  });
  useEffect(() => {
    if (session.data && picked === null) setPicked(new Set(session.data.assets.map((a) => a.externalId)));
  }, [session.data, picked]);
  const done = useMutation({
    mutationFn: () =>
      api<{ accounts: { id: string; displayName: string; subscribed: boolean; error: string | null }[] }>(
        `/v1/connect/sessions/${sessionId}/accounts`,
        { method: 'POST', body: JSON.stringify({ externalIds: [...(picked ?? [])] }) },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
  const close = () => {
    setParams({}, { replace: true });
    setPicked(null);
    done.reset();
  };
  if (!sessionId && !failed) return null;
  if (!sessionId && failed)
    return (
      <div className="notice warn" role="alert" style={{ marginBottom: '1rem' }} data-testid="connect-error">
        <span className="grow">
          {t(`connect.error.${failed}`, { defaultValue: t('connect.error.platform_error') })}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={close}>
          {t('common.close')}
        </button>
      </div>
    );
  const s = session.data;
  const title = s
    ? t('connect.chooseTitle', { platform: t(`connect.platform.${s.connector}`) })
    : t('common.loading');
  return (
    <Dialog
      open
      wide
      title={title}
      onClose={close}
      onSubmit={done.data ? close : () => done.mutate()}
      testId="connect-choose"
      footer={
        done.data ? (
          <button type="submit" className="btn btn-grad">
            {t('common.close')}
          </button>
        ) : s?.status === 'authorised' ? (
          <>
            <button type="button" className="btn btn-ghost" onClick={close}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-grad"
              disabled={!picked?.size || done.isPending}
              data-testid="connect-selected"
            >
              {t('connect.connectSelected', { count: picked?.size ?? 0 })}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-grad" onClick={close}>
            {t('common.close')}
          </button>
        )
      }
    >
      {session.isLoading && <Loading />}
      {session.isError && <ErrorState error={session.error} />}
      {s && (failed || s.status === 'failed' || s.status === 'expired') && (
        <div className="notice warn" role="alert" data-testid="connect-error">
          {t(`connect.error.${failed ?? (s.status === 'expired' ? 'expired' : 'platform_error')}`, {
            defaultValue: t('connect.error.platform_error'),
          })}
          {s.error && <span className="faint"> ({s.error})</span>}
        </div>
      )}
      {s?.status === 'authorised' && !done.data && (
        <>
          <p className="mute" style={{ marginTop: 0 }}>
            {t('connect.chooseHint')}
          </p>
          <ul className="list-plain connect-assets" data-testid="connect-assets">
            {s.assets.map((a) => (
              <li key={`${a.provider}:${a.externalId}`}>
                <label className="connect-asset">
                  <input
                    type="checkbox"
                    checked={picked?.has(a.externalId) ?? true}
                    onChange={(e) => {
                      const next = new Set(picked ?? []);
                      if (e.target.checked) next.add(a.externalId);
                      else next.delete(a.externalId);
                      setPicked(next);
                    }}
                  />
                  <ChannelIcon channel={iconFor(a.provider)} size={14} />
                  <span className="grow">
                    <strong dir="auto">{a.displayName}</strong>
                    <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                      {t(`connect.kind.${a.kind}`)}
                      {a.connected && ` · ${t('connect.alreadyConnected')}`}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {done.isError && <ErrorState error={done.error} />}
        </>
      )}
      {done.data && (
        <ul className="list-plain connect-assets" data-testid="connect-done">
          {done.data.accounts.map((a) => (
            <li key={a.id} className="connect-asset">
              <CheckCircle2 size={16} color={a.subscribed ? 'var(--success)' : 'var(--warning)'} />
              <span className="grow" dir="auto">
                <strong>{a.displayName}</strong>
                <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                  {a.subscribed ? t('connect.connected') : t('connect.connectedNoEvents')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

/** Logo for an account kind (stores and mailboxes use their own brand; channels their channel mark). */
export const iconFor = (provider: string) =>
  ({
    meta_facebook: 'facebook',
    meta_instagram: 'instagram',
    whatsapp_cloud: 'whatsapp',
    tiktok_business: 'tiktok',
    x_account: 'x',
    linkedin_page: 'linkedin',
    email_smtp_imap: 'email',
    sms_http: 'sms',
    ly_voice: 'voice',
    snapchat_leads: 'snapchat',
  })[provider] ?? provider;
